import PropTypes from "prop-types";
import * as React from "react";
import { Button } from "../../themeV2";
import { usd } from "../../util/formatCurrency";
import EditableCustomerText from "./EditableCustomerText";
import s from "./PurchasedServices.module.css";

const PurchasedServices = React.memo(function PurchasedServices({
  services,
  showPricing = false,
  editable = false,
  onUpdateText,
  canRefund = false,
  onRefundService,
}) {
  if (services.length === 0) return null;

  return (
    <ul className={s.root}>
      {services.map((service) => {
        const isRefunded = Boolean(service.refunded);
        const amount = (service.splitAmount ?? 0) + (service.bumpAmount ?? 0);
        const showRefundButton = canRefund && !isRefunded && onRefundService;

        /*
         * "Refund $0.00" would promise a refund that is not happening, so a
         * free service is named for its effect. Visible text stays inside the
         * accessible name either way (WCAG 2.5.3 Label in Name).
         */
        const refundText = amount > 0 ? `Refund ${usd(amount)}` : "Remove";
        const refundLabel =
          amount > 0
            ? `Refund ${usd(amount)} for ${service.label}`
            : `Remove ${service.label}, no charge`;

        return (
          <li key={service.purchaseServiceId} className={s.item}>
            <span className={s.labelRow}>
              <span>
                <span className={isRefunded ? s.strikethrough : undefined}>
                  {service.label}
                </span>
                {isRefunded && (
                  <>
                    {" "}
                    <span className={s.refundedBadge}>(Refunded)</span>
                  </>
                )}
              </span>
              <span className={s.priceRow}>
                {showPricing && (
                  <span className={isRefunded ? s.strikethrough : undefined}>
                    {usd(amount)}
                  </span>
                )}
                {showRefundButton && (
                  <Button
                    size="xs"
                    variant="outlined"
                    color="error"
                    onClick={() =>
                      onRefundService(
                        service.purchaseServiceId,
                        service.label,
                        amount,
                      )
                    }
                    aria-label={refundLabel}
                  >
                    {refundText}
                  </Button>
                )}
              </span>
            </span>
            {/*
              Gate on requiresTextInput, not on whether text happens to exist —
              a required-text service the customer left blank is exactly the
              case an admin needs to fill in.
            */}
            {!isRefunded &&
            editable &&
            onUpdateText &&
            service.requiresTextInput ? (
              <EditableCustomerText
                service={service}
                onUpdateText={onUpdateText}
              />
            ) : (
              service.customerText && (
                <p
                  className={`${s.customerText} ${
                    isRefunded ? s.refundedText : ""
                  }`}
                >
                  {service.customerText}
                </p>
              )
            )}
          </li>
        );
      })}
    </ul>
  );
});

PurchasedServices.propTypes = {
  services: PropTypes.arrayOf(
    PropTypes.shape({
      purchaseServiceId: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      category: PropTypes.string,
      requiresTextInput: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.number,
      ]),
      splitAmount: PropTypes.number,
      bumpAmount: PropTypes.number,
      customerText: PropTypes.string,
      refunded: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    }),
  ).isRequired,
  showPricing: PropTypes.bool,
  editable: PropTypes.bool,
  onUpdateText: PropTypes.func,
  canRefund: PropTypes.bool,
  onRefundService: PropTypes.func,
};

export default PurchasedServices;
